import { AsyncPipe, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { WA_WINDOW } from '@ng-web-apis/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { nonNullable, rmaShareReplay } from '@rma/common-ui-rxjs/operators';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { ShareButtonComponent } from '@rma/generic/ui/share/ui-share-buttton';
import { StaticImageUrlPipe } from '@rma/generic/util/environment';
import { Observable, combineLatest, filter, map, skip, startWith, take, tap, withLatestFrom } from 'rxjs';
import { FindMyAgentStore } from './find-my-agent.store';

@UntilDestroy()
@Component({
  selector: 'rma-find-my-agent',
  imports: [AsyncPipe, RouterModule, StaticImageUrlPipe, TranslocoModule, RmaIconComponent, ShareButtonComponent, MatButtonModule],
  providers: [provideTranslocoScope('findMyAgent'), FindMyAgentStore],
  templateUrl: './find-my-agent.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block h-screen bg-white md:bg-blue-10',
  },
})
export class FindMyAgentComponent {
  private location = inject(Location);

  private readonly router = inject(Router);
  private readonly window = inject(WA_WINDOW);
  protected readonly store = inject(FindMyAgentStore);
  public readonly activatedRoute = inject(ActivatedRoute);

  public isStepPage$: Observable<boolean>;
  public showHeader$: Observable<boolean>;
  public showRestore$: Observable<boolean>;
  public shareData$: Observable<ShareData>;
  public showBackButton$: Observable<boolean>;
  public returnPageName$: Observable<string>;
  public stepNumber$: Observable<number>;
  public stepTotal: number;
  public contentStyle$: Observable<string>;
  public showStepper$: Observable<boolean>;

  constructor() {
    const routerEnd$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(true),
      rmaShareReplay(),
    );

    const routeData$ = routerEnd$.pipe(map(() => this.activatedRoute.snapshot.firstChild?.data ?? {}));

    this.shareData$ = routerEnd$.pipe(map((_) => ({ url: this.window.location.href })));

    this.showHeader$ = routeData$.pipe(map(({ hideHeader }) => (hideHeader === undefined ? true : !hideHeader)));

    this.isStepPage$ = routeData$.pipe(map(({ nonStep }) => (nonStep === undefined ? true : !nonStep)));

    this.contentStyle$ = this.isStepPage$.pipe(
      untilDestroyed(this),
      map((isStepPage) => (isStepPage ? 'md:max-w-3xl p-6 md:p-0 mx-auto md:bg-white md:rounded-lg md:p-16' : '')),
    );

    this.showBackButton$ = combineLatest([this.isStepPage$, routerEnd$]).pipe(
      map(([isStepPage, _]) => isStepPage && this.router.url !== '/whyareyouhere'),
    );

    this.showRestore$ = routeData$.pipe(map(({ initalStep }) => !!initalStep));

    this.stepTotal = this.store.steps.filter(function (step) {
      return step.indexOf('renting') && step.indexOf('buying') && step.indexOf('results');
    }).length;

    this.stepNumber$ = routerEnd$.pipe(
      map(() => this.router.url.toString().slice(1).split('?')[0]),
      map((segment) => this.store.steps.findIndex((s) => s === segment) + 1),
    );

    this.showStepper$ = combineLatest([this.isStepPage$, this.stepNumber$]).pipe(
      map(([isStepPage, stepNumber]) => isStepPage && stepNumber < 7),
    );

    this.returnPageName$ = routerEnd$.pipe(map(() => this.router.url.toString().slice(1).split('?')[0]));

    this.handleStep();
    this.rehyradateFromRouter();
  }

  public stepBack() {
    if (this.location.back() === undefined) {
      this.router.navigate(['/whyareyouhere']);
    } else {
      this.location.back();
    }
  }

  onRestore(params: Params) {
    this.patchStoreFromQueryParams(params);
    this.router.navigate(['results'], { queryParams: params });
  }

  private handleStep() {
    this.store.nextStep$
      .pipe(untilDestroyed(this), skip(1), nonNullable(), withLatestFrom(this.store.queryParams$))
      .subscribe(([nextStep, queryParams]) => {
        this.router.navigate([nextStep], { queryParams, relativeTo: this.activatedRoute });
        this.store.patchState({ nextStep: undefined });
        this.store.clearSaveEffect(true);
      });
  }

  private rehyradateFromRouter() {
    this.activatedRoute.queryParams
      .pipe(
        take(1),
        tap((queryParams) => this.patchStoreFromQueryParams(queryParams)),
      )
      .subscribe();
  }

  private patchStoreFromQueryParams(queryParams: Params) {
    const paramsLength = Object.keys(queryParams).length;

    // no params so can't rehyradate
    if (!paramsLength) {
      this.router.navigate(['.'], { relativeTo: this.activatedRoute });
      return;
    }

    // we could add logic to check if query params exist in store state interface keys
    this.store.patchState(queryParams);
  }
}
